import { WorkItemState } from '@robocorp/robocloud-search-iternal-sdk';
import { z } from 'zod';
import { addDays } from '@robocorp/robocloud-common/lib/utils/timeUtils';
import { ApiLinks } from '~/constants';
import { WorkspaceKeyPermission } from '~/types/WorkspaceKeyTypes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiParam<
  Schema extends z.Schema<any, any> = z.Schema<any, any>,
  Option extends string = string,
> = {
  type: 'boolean' | 'number' | 'enum' | 'string';
  name: string;
  label: string;
  default: boolean | number | string | undefined;
  schema?: Schema;
  options?: Option[];
};

export type ApiOption = {
  translationKey: string;
  permission:
    | WorkspaceKeyPermission
    | 'read_task_packages'
    | 'write_task_packages'
    | 'read_workers'
    | 'write_workers';
  endpoint: string;
  method: string;
  api: 'process' | 'robot' | 'worker' | 'assistant' | 'search';
  optionalData?: string;
  customData?: boolean;
  new?: boolean;
  params?: ApiParam[];
  serverOverride?: string;
};

export type ApiEndpoint = {
  translationKey: string;
  server: string;
  options: ApiOption[];
};

export type ApiEndpoints = Record<string, ApiEndpoint>;

const inSevenDaysISO = addDays(new Date(), 7).toISOString();

export const apiOptions: ApiEndpoints = {
  process: {
    translationKey: 'processApi.title',
    server: ApiLinks.PROCESS_API,
    options: [
      {
        translationKey: 'processApi.listProcesses',
        permission: 'read_processes',
        endpoint: '/processes',
        method: 'GET',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.startProcessSinglePayload',
        permission: 'trigger_processes',
        endpoint: '/processes/$processId/runs',
        method: 'POST',
        api: 'process',
        optionalData: '{ "message": "Greetings from API Helper single payload" }',
        customData: true,
      },
      {
        translationKey: 'processApi.startBatchPayloadProcess',
        permission: 'trigger_processes',
        endpoint: '/processes/$processId/runs-batch',
        method: 'POST',
        api: 'process',
        optionalData: '[{ "message": "Batch Start" }, { "message": "From API Helper" }]',
        customData: true,
      },
      {
        translationKey: 'processApi.startDefaultProcess',
        permission: 'trigger_processes',
        endpoint: '/processes/$processId/run-request',
        method: 'POST',
        api: 'process',
        optionalData:
          '{ "type": "default", "callbackUrl": "https://your.callback.url", "secret": "your-callback-secret", "eventsToSend": ["process_run.completed"] }',
        customData: true,
      },
      {
        translationKey: 'processApi.stopProcessRun',
        permission: 'trigger_processes',
        endpoint: '/processes/$processId/runs/$processRunId/stop',
        method: 'POST',
        api: 'process',
      },
      {
        translationKey: 'processApi.listWorkspaceProcessRunsApiHelper',
        permission: 'read_runs',
        endpoint: '/pruns',
        method: 'GET',
        api: 'process',
        params: [
          {
            type: 'number',
            name: 'limit',
            label: 'APIHelper.limit',
            default: 10,
          },
        ],
      },
      {
        translationKey: 'processApi.listProcessRunsApiHelper',
        permission: 'read_runs',
        endpoint: '/processes/$processId/runs',
        method: 'GET',
        api: 'process',
        new: false,
        params: [
          {
            type: 'number',
            name: 'limit',
            label: 'APIHelper.limit',
            default: 10,
          },
        ],
      },
      {
        translationKey: 'processApi.getProcessRunStatus',
        permission: 'read_runs',
        endpoint: '/processes/$processId/runs/$processRunId',
        method: 'GET',
        api: 'process',
      },
      {
        translationKey: 'processApi.getProcessRunWorkItemsSummary',
        permission: 'read_processes',
        endpoint: '/processes/$processId/runs/$processRunId/work-items-summary',
        method: 'GET',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.getWorkItem',
        permission: 'read_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId',
        method: 'GET',
        api: 'process',
        new: false,
        params: [
          {
            type: 'boolean',
            name: 'includeData',
            label: 'APIHelper.includeData',
            default: true,
          },
        ],
      },
      {
        translationKey: 'processApi.listProcessWorkItems',
        permission: 'read_work_items',
        endpoint: '/processes/$processId/work-items',
        method: 'GET',
        api: 'process',
        new: false,
        params: [
          {
            type: 'boolean',
            name: 'includeData',
            label: 'APIHelper.includeData',
            default: true,
          },
          {
            type: 'number',
            name: 'size',
            label: 'APIHelper.size',
            default: 100,
            schema: z.number().min(1).max(200).default(100),
          },
          {
            type: 'enum',
            name: 'state',
            label: 'APIHelper.state',
            default: undefined,
            schema: z
              .enum([
                WorkItemState.New,
                WorkItemState.Completed,
                WorkItemState.Failed,
                WorkItemState.InProgress,
                WorkItemState.Pending,
              ])
              .optional(),
            options: [
              WorkItemState.New,
              WorkItemState.Completed,
              WorkItemState.Failed,
              WorkItemState.InProgress,
              WorkItemState.Pending,
            ],
          },
          {
            type: 'string',
            name: 'search',
            label: 'APIHelper.search',
            default: undefined,
          },
          {
            type: 'enum',
            name: 'sortBy',
            label: 'APIHelper.sortBy',
            default: 'stateTs',
            schema: z.enum(['createTs', 'stateTs', 'state', 'filesCount']).optional(),
            options: ['createTs', 'stateTs', 'state', 'filesCount'],
          },
          {
            type: 'enum',
            name: 'sortOrder',
            label: 'APIHelper.sortOrder',
            default: 'desc',
            schema: z.enum(['desc', 'asc']).optional(),
            options: ['desc', 'asc'],
          },
          {
            type: 'string',
            name: 'cursor',
            label: 'APIHelper.cursor',
            default: undefined,
          },
        ],
      },
      {
        translationKey: 'processApi.listProcessRunWorkItems',
        permission: 'read_work_items',
        endpoint: '/processes/$processId/runs/$processRunId/work-items',
        method: 'GET',
        api: 'process',
        new: false,
        params: [
          {
            type: 'boolean',
            name: 'includeData',
            label: 'APIHelper.includeData',
            default: true,
          },
          {
            type: 'number',
            name: 'size',
            label: 'APIHelper.size',
            default: 100,
            schema: z.number().min(1).max(200).default(100),
          },
          {
            type: 'enum',
            name: 'state',
            label: 'APIHelper.state',
            default: undefined,
            schema: z
              .enum([
                WorkItemState.New,
                WorkItemState.Completed,
                WorkItemState.Failed,
                WorkItemState.InProgress,
                WorkItemState.Pending,
              ])
              .optional(),
            options: [
              WorkItemState.New,
              WorkItemState.Completed,
              WorkItemState.Failed,
              WorkItemState.InProgress,
              WorkItemState.Pending,
            ],
          },
          {
            type: 'string',
            name: 'search',
            label: 'APIHelper.search',
            default: undefined,
          },
          {
            type: 'enum',
            name: 'sortBy',
            label: 'APIHelper.sortBy',
            default: 'stateTs',
            schema: z.enum(['createTs', 'stateTs', 'state', 'filesCount']).optional(),
            options: ['createTs', 'stateTs', 'state', 'filesCount'],
          },
          {
            type: 'enum',
            name: 'sortOrder',
            label: 'APIHelper.sortOrder',
            default: 'desc',
            schema: z.enum(['desc', 'asc']).optional(),
            options: ['desc', 'asc'],
          },
          {
            type: 'string',
            name: 'cursor',
            label: 'APIHelper.cursor',
            default: undefined,
          },
        ],
      },
      {
        translationKey: 'processApi.createWorkItemInput',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items',
        method: 'POST',
        api: 'process',
        optionalData: '{"payload": { "foo": "bar" }}',
        customData: true,
        new: false,
      },
      {
        translationKey: 'processApi.updateWorkItemPayload',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId/data',
        method: 'PUT',
        api: 'process',
        optionalData: '{ "updated": true }',
        customData: true,
        new: false,
      },
      {
        translationKey: 'processApi.getWorkItemFileDownloadLink',
        permission: 'read_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId/files/$fileId/download',
        method: 'GET',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.getWorkItemFileUploadLink',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId/files/upload',
        method: 'POST',
        api: 'process',
        optionalData: '{ "fileName": "foo", "fileSize": 123 }',
        customData: true,
        new: false,
      },
      {
        translationKey: 'processApi.retryWorkItem',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId/retry',
        method: 'POST',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.completeWorkItem',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId/complete',
        method: 'POST',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.deleteWorkItem',
        permission: 'write_work_items',
        endpoint: '/processes/$processId/work-items/$workItemId',
        method: 'DELETE',
        api: 'process',
        new: false,
      },
      {
        translationKey: 'processApi.listRobotRuns',
        permission: 'read_runs',
        endpoint: '/processes/$processId/runs/$processRunId/robotRuns',
        method: 'GET',
        api: 'process',
        params: [
          {
            type: 'number',
            name: 'limit',
            label: 'APIHelper.limit',
            default: 10,
          },
        ],
      },
      {
        translationKey: 'processApi.getRobotRun',
        permission: 'read_runs',
        endpoint: '/processes/$processId/runs/$processRunId/robotRuns/$robotRunId',
        method: 'GET',
        api: 'process',
      },
      {
        translationKey: 'processApi.listRobotRunEvents',
        permission: 'read_run_outputs',
        endpoint: '/processes/$processId/runs/$processRunId/robotRuns/$robotRunId/events',
        method: 'GET',
        api: 'process',
      },
      {
        translationKey: 'processApi.listRobotRunConsoleMessages',
        permission: 'read_run_outputs',
        endpoint: '/processes/$processId/runs/$processRunId/robotRuns/$robotRunId/messages',
        method: 'GET',
        api: 'process',
      },
      {
        translationKey: 'processApi.listRobotRunArtifacts',
        permission: 'read_run_outputs',
        endpoint: '/processes/$processId/runs/$processRunId/robotRuns/$robotRunId/artifacts',
        method: 'GET',
        api: 'process',
      },
      {
        translationKey: 'processApi.getRobotArtifact',
        permission: 'read_run_outputs',
        endpoint:
          '/processes/$processId/runs/$processRunId/robotRuns/$robotRunId/artifacts/$artifactId/$fileName',
        method: 'GET',
        api: 'process',
      },
    ],
  },
  robot: {
    translationKey: 'robotApi',
    server: ApiLinks.ROBOT_API,
    options: [
      {
        translationKey: 'robot.add',
        permission: 'write_task_packages',
        endpoint: '/robots',
        method: 'POST',
        api: 'robot',
        optionalData: '{ "name": "Test robot" }',
      },
      {
        translationKey: 'getLinkToUploadRobot',
        permission: 'write_task_packages',
        endpoint: '/robots/$robotId/file/uploadlink',
        method: 'GET',
        api: 'robot',
      },
      {
        translationKey: 'getLinkToDownloadRobot',
        permission: 'read_task_packages',
        endpoint: '/robots/$robotId/file/downloadlink',
        method: 'GET',
        api: 'robot',
      },
    ],
  },
  worker: {
    translationKey: 'workerApi.title',
    server: ApiLinks.EXTERNAL_API,
    options: [
      {
        translationKey: 'workerApi.listWorkers',
        permission: 'read_workers',
        endpoint: '/workers',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.generateLinkToken',
        permission: 'write_workers',
        endpoint: '/workers/link-tokens',
        method: 'POST',
        api: 'worker',
        new: true,
        optionalData: `{ "expires_at": "${inSevenDaysISO}" }`,
        customData: true,
      },
      {
        translationKey: 'workerApi.getWorker',
        permission: 'read_workers',
        endpoint: '/workers/$workerId',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.updateWorker',
        permission: 'write_workers',
        endpoint: '/workers/$workerId',
        method: 'PUT',
        api: 'worker',
        new: true,
        optionalData: '{ "name": "Updated Worker Name" }',
        customData: true,
      },
      {
        translationKey: 'workerApi.deleteWorker',
        permission: 'write_workers',
        endpoint: '/workers/$workerId',
        method: 'DELETE',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.getWorkerGroup',
        permission: 'read_workers',
        endpoint: '/worker-groups/$workerGroupId',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.deleteWorkerGroup',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId',
        method: 'DELETE',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.updateWorkerGroup',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId',
        method: 'PUT',
        api: 'worker',
        new: true,
        optionalData: '{ "name": "Updated Worker Group Name" }',
        customData: true,
      },
      {
        translationKey: 'workerApi.deleteWorkerGroupLinkToken',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId/link-tokens/$linkTokenId',
        method: 'DELETE',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.getWorkerGroupLinkToken',
        permission: 'read_workers',
        endpoint: '/worker-groups/$workerGroupId/link-tokens/$linkTokenId',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.listWorkerGroupLinkTokens',
        permission: 'read_workers',
        endpoint: '/worker-groups/$workerGroupId/link-tokens',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.updateWorkerGroupLinkToken',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId/link-tokens/$linkTokenId',
        method: 'PUT',
        api: 'worker',
        new: true,
        optionalData: `{ "name": "My Updated Worker Group Link Token", "expires_at": "${inSevenDaysISO}" }`,
        customData: true,
      },
      {
        translationKey: 'workerApi.createWorkerGroupToken',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId/link-tokens',
        method: 'POST',
        api: 'worker',
        new: true,
        optionalData: `{ "name": "My Worker Group Link Token", "expires_at": "${inSevenDaysISO}" }`,
        customData: true,
      },
      {
        translationKey: 'workerApi.listWorkerGroups',
        permission: 'read_workers',
        endpoint: '/worker-groups',
        method: 'GET',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.createWorkerGroup',
        permission: 'write_workers',
        endpoint: '/worker-groups',
        method: 'POST',
        api: 'worker',
        new: true,
        optionalData: '{ "name": "New Worker Group" }',
        customData: true,
      },
      {
        translationKey: 'workerApi.addWorkerToGroup',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId/workers/$workerId',
        method: 'POST',
        api: 'worker',
        new: true,
      },
      {
        translationKey: 'workerApi.removeWorkerFromGroup',
        permission: 'write_workers',
        endpoint: '/worker-groups/$workerGroupId/workers/$workerId',
        method: 'DELETE',
        api: 'worker',
        new: true,
      },
    ],
  },
  assistant: {
    translationKey: 'assistantApi',
    server: ApiLinks.ASSISTANT_API,
    options: [
      {
        translationKey: 'listAssistants',
        permission: 'read_assistants',
        endpoint: '/assistants',
        method: 'GET',
        api: 'assistant',
      },
      {
        translationKey: 'getAssistant',
        permission: 'read_assistants',
        endpoint: '/assistants/$assistantId',
        method: 'GET',
        api: 'assistant',
      },
      {
        translationKey: 'createAssistant',
        permission: 'write_assistants',
        endpoint: '/assistants',
        method: 'POST',
        api: 'assistant',
        optionalData: '{"name": "Test assistant", "robotId": "{robotId}", "task": "{robotTask}"}',
      },
      {
        translationKey: 'listAssistantRuns',
        permission: 'read_assistants',
        endpoint: '/assistants/$assistantId/runs',
        method: 'GET',
        api: 'assistant',
        new: false,
        serverOverride: ApiLinks.LEGACY_EXTERNAL_API,
        params: [
          {
            type: 'number',
            name: 'size',
            label: 'APIHelper.size',
            default: 50,
            schema: z.number().min(1).max(200).default(50),
          },
          {
            type: 'enum',
            name: 'sortBy',
            label: 'APIHelper.sortBy',
            default: 'createTs',
            schema: z.enum(['createTs']).optional(),
            options: ['createTs'],
          },
          {
            type: 'enum',
            name: 'sortOrder',
            label: 'APIHelper.sortOrder',
            default: 'desc',
            schema: z.enum(['desc', 'asc']).optional(),
            options: ['desc', 'asc'],
          },
          {
            type: 'string',
            name: 'cursor',
            label: 'APIHelper.cursor',
            default: undefined,
          },
          {
            type: 'string',
            name: 'createTs.gte',
            label: 'APIHelper.startDate',
            default: addDays(new Date(), -90).toISOString(),
          },
          {
            type: 'string',
            name: 'createTs.lte',
            label: 'APIHelper.endDate',
            default: new Date().toISOString(),
          },
        ],
      },
    ],
  },
};
